import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { BallMark } from "../App";
import { InlineSignIn } from "../components/InlineSignIn";
import { Button, ErrorNote, Icon, Skeleton, cx } from "../components/ui";

/**
 * Where a claim link (`/claim/:token`) lands. Reachable at any auth state, including no
 * Firebase account at all — the whole point of claiming is that it *creates* a profile for
 * someone who doesn't have one, so this can't wait behind the normal sign-in/username gates in
 * App.tsx (see the early return there).
 *
 * Once signed in, whether or not a profile already exists, the claim runs automatically — there
 * is no separate "confirm" step, since the token itself (sent only to the intended person) is
 * the authorization.
 */
export function ClaimPage({ token }: { token: string }) {
  const { firebaseUser, isLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [claimed, setClaimed] = useState<{ displayName: string; skipped: number } | null>(null);

  const claim = useMutation({
    mutationFn: () => api.auth.claim(token),
    onSuccess: async ({ user, skippedCompetitionIds }) => {
      analytics.track(AnalyticsEvent.ProfileCreated, { claimed: true });
      await refresh();
      setClaimed({ displayName: user.displayName, skipped: skippedCompetitionIds.length });
    },
    onError: (error) => trackApiError(error, "claim_managed_player"),
  });

  const { mutate: runClaim, isPending, isSuccess, isError } = claim;

  useEffect(() => {
    if (firebaseUser && !isPending && !isSuccess && !isError) runClaim();
  }, [firebaseUser, isPending, isSuccess, isError, runClaim]);

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden p-4 sm:p-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 12%, var(--t-accent-soft), transparent 46%)",
        }}
      />

      <div className="card animate-in relative w-full max-w-md p-6 sm:p-8">
        <div className="mb-8 flex items-center gap-2.5 text-lg font-bold text-fg">
          <BallMark size={30} />
          Last Man Standing
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24" />
          </div>
        ) : claimed ? (
          <ClaimedView
            displayName={claimed.displayName}
            skipped={claimed.skipped}
            onContinue={() => navigate("/")}
          />
        ) : firebaseUser ? (
          <ClaimingView pending={isPending} error={claim.error} onRetry={() => runClaim()} />
        ) : (
          <InlineSignIn
            eyebrow="You've been invited"
            title="Sign in to claim your spot"
            subtitle="Sign in or create an account, and your picks and history transfer automatically."
          />
        )}
      </div>
    </div>
  );
}

function ClaimingView({
  pending,
  error,
  onRetry,
}: {
  pending: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <div className="space-y-4 text-center">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-danger-soft text-danger-text">
          <Icon name="close" className="size-6" />
        </span>
        <h1 className="display-title text-2xl text-fg">Couldn't claim that link</h1>
        <ErrorNote>
          {error instanceof ApiError
            ? error.message
            : "Something went wrong. Try again, or ask the admin for a fresh link."}
        </ErrorNote>
        <Button block onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-accent-soft text-accent-text">
        <Icon name="refresh" className={cx("size-6", pending && "animate-spin")} />
      </span>
      <p className="label text-accent-text">Claiming your spot</p>
      <Skeleton className="h-10" />
    </div>
  );
}

function ClaimedView({
  displayName,
  skipped,
  onContinue,
}: {
  displayName: string;
  skipped: number;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-4 text-center">
      <span className="animate-pop mx-auto grid size-14 place-items-center rounded-2xl bg-accent text-accent-fg">
        <Icon name="check" className="size-7" strokeWidth={2.6} />
      </span>
      <h1 className="display-title text-3xl text-fg">You're in, {displayName}.</h1>
      <p className="text-sm leading-relaxed text-fg-subtle">
        That player's picks and history are yours now — pick up right where they left off.
      </p>

      {skipped > 0 && (
        <p className="rounded-xl border border-warn-line bg-warn-soft px-3.5 py-2.5 text-left text-[13px] leading-relaxed text-warn-text">
          {skipped} {skipped === 1 ? "competition was" : "competitions were"} skipped because you
          were already in {skipped === 1 ? "it" : "them"} under your own account — ask the admin
          to sort that one out.
        </p>
      )}

      <Button block onClick={onContinue}>
        Go to your competitions
        <Icon name="arrowRight" className="size-4" />
      </Button>
    </div>
  );
}
